import React, { useState } from "react";
import { Link } from "react-router-dom";
import { EmailDecryptModal } from "../components/common/EmailDecryptModal";
import styles from "./Services.module.css";

interface Service {
  id: string;
  title: string;
  description: string;
  price: string;
  features: string[];
}

const services: Service[] = [
  {
    id: "wedding",
    title: "Wedding Photography",
    description:
      "Your wedding day deserves to be captured with elegance and care. From the first look to the last dance, we document every moment with artistry and precision.",
    price: "£2,500 – £5,000",
    features: [
      "Full-day coverage (8-10 hours)",
      "Professional editing and retouching",
      "Private online gallery",
      "High-resolution digital files",
      "Print rights included",
      "Second photographer available"
    ]
  },
  {
    id: "portrait",
    title: "Portrait Sessions",
    description:
      "Whether for personal branding, family memories, or professional headshots, our portrait sessions are designed to capture your authentic self in the best light.",
    price: "£300 – £800",
    features: [
      "1-2 hour session",
      "Multiple outfit changes",
      "Location scouting and selection",
      "Professional retouching",
      "Digital gallery delivery",
      "Printing options available"
    ]
  },
  {
    id: "commercial",
    title: "Commercial Photography",
    description:
      "High-quality imagery that elevates your brand. We work with businesses of all sizes to create compelling visuals that tell your story and connect with your audience.",
    price: "£1,000 – £3,500",
    features: [
      "Product and lifestyle photography",
      "Corporate headshots",
      "Brand storytelling sessions",
      "Commercial usage rights",
      "Fast turnaround times",
      "Team and location shoots"
    ]
  },
  {
    id: "event",
    title: "Event Photography",
    description:
      "From corporate gatherings to private celebrations, we provide professional coverage that captures the energy, emotion, and important moments of your event.",
    price: "£500 – £2,000",
    features: [
      "Flexible hourly packages",
      "Candid and posed coverage",
      "Same-day preview gallery",
      "Digital delivery within 48 hours",
      "Shareable online gallery",
      "Print options available"
    ]
  },
  {
    id: "nature",
    title: "Nature & Landscape",
    description:
      "Breathtaking images that capture the beauty of the natural world. From golden hour landscapes to wildlife encounters, we bring the outdoors to life through photography.",
    price: "£400 – £1,200",
    features: [
      "Location-based shoots",
      "Golden hour and sunrise sessions",
      "Print-ready high-resolution files",
      "Digital gallery delivery",
      "Fine art printing available",
      "Travel packages offered"
    ]
  },
  {
    id: "family",
    title: "Family Photography",
    description:
      "Preserve the moments that matter most with your loved ones. Our family sessions are relaxed, natural, and focused on capturing genuine connections and joy.",
    price: "£350 – £700",
    features: [
      "1-2 hour session",
      "Up to 6 family members",
      "Indoor or outdoor locations",
      "Professional editing included",
      "Digital gallery delivery",
      "Print packages available"
    ]
  }
];

export const Services: React.FC = () => {
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  return (
    <div className={styles.services}>
      <div className={styles.hero}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>Services</h1>
          <p className={styles.heroSubtitle}>
            Professional photography tailored to your needs
          </p>
        </div>
      </div>

      <div className={styles.servicesGrid}>
        {services.map((service) => (
          <div key={service.id} className={styles.serviceCard}>
            <h2 className={styles.serviceTitle}>{service.title}</h2>
            <p className={styles.serviceDescription}>{service.description}</p>
            <div className={styles.servicePrice}>{service.price}</div>
            <ul className={styles.serviceFeatures}>
              {service.features.map((feature, index) => (
                <li key={index} className={styles.serviceFeature}>
                  {feature}
                </li>
              ))}
            </ul>
            <button
              className={styles.serviceButton}
              onClick={() => setIsEmailModalOpen(true)}
            >
              Inquire Now
            </button>
          </div>
        ))}
      </div>

      <div className={styles.cta}>
        <div className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>Ready to book your session?</h2>
          <p className={styles.ctaText}>
            Contact us today to discuss your photography needs and receive a
            personalized quote.
          </p>
          <button
            className={styles.ctaButton}
            onClick={() => setIsEmailModalOpen(true)}
          >
            Get in Touch
          </button>
        </div>
      </div>

      <EmailDecryptModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
      />
    </div>
  );
};
